import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { useVirtualizer } from "@tanstack/react-virtual";
import { fetchAlertsData } from "./api";

const formatAlertDate = (value) => {
  if (!value) return value;
  const date = new Date(value);
  if (isNaN(date.getTime())) return value;
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${month}-${day}-${date.getUTCFullYear()}`;
};

const formatDatesInText = (value) =>
  typeof value === "string"
    ? value.replace(
        /(\d{4})-(\d{2})-(\d{2})(?:[ T]\d{2}:\d{2}:\d{2})?/g,
        "$2-$3-$1",
      )
    : value;

const ChiliIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="32"
    height="32"
    fill="#C20000"
    viewBox="0 0 256 256"
    className="chili-icon"
  >
    <path d="M167.27,40.42A40.06,40.06,0,0,0,128,8a8,8,0,0,0,0,16,24,24,0,0,1,22.85,16.66A64.08,64.08,0,0,0,96,104c0,46.75-25.75,78-76.53,93a16,16,0,0,0,1.77,31.13A264.8,264.8,0,0,0,66.75,232c40.78,0,86.16-9.15,117.53-35.46C210.64,174.44,224,143.3,224,104h0A64.07,64.07,0,0,0,167.27,40.42ZM192,95,163.58,80.83a8,8,0,0,0-7.16,0L128,95l-13.37-6.68a48,48,0,0,1,90.74,0Z"></path>
  </svg>
);

// Funzione helper per formattare i valori numerici o mostrare N/A
const formatAlertValue = (val) => {
  if (val === null || val === undefined || val === "") {
    return "N/A";
  }

  const strVal = String(val);

  if (strVal.includes("%")) {
    return strVal;
  }

  const num = parseFloat(val);

  if (isNaN(num)) return "N/A";

  return num.toLocaleString("en-US", {
    maximumFractionDigits: 0,
  });
};

const ROW_HEIGHT = 40;
const GRID_COLUMNS = "1fr 1.5fr 0.8fr 0.8fr 0.8fr 0.8fr 1.2fr 0.8fr 1fr";

function AlertingPage() {
  // Stati per i dati e il caricamento
  const [alertsData, setAlertsData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Stati per i filtri esistenti
  const [classFilter, setClassFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [entityFilter, setEntityFilter] = useState("");
  const [selectedLevel, setSelectedLevel] = useState(0);
  const [hoveredLevel, setHoveredLevel] = useState(0);

  // Stato per l'ordinamento
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  const scrollContainerRef = useRef(null);

  // FUNZIONE DI CARICAMENTO DATI
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};

      if (sortConfig.key && sortConfig.direction) {
        let dbColumnName = sortConfig.key;

        if (dbColumnName === "alert_class") {
          dbColumnName = "class";
        } else if (dbColumnName === "alert_type") {
          dbColumnName = "type";
        } else if (dbColumnName === "expected") {
          dbColumnName = "expected";
        } else if (dbColumnName === "actual") {
          dbColumnName = "actual";
        } else if (dbColumnName === "target") {
          dbColumnName = "target";
        }

        params.order_by = dbColumnName;
        params.order = sortConfig.direction;
      }

      const response = await fetchAlertsData(params);
      setAlertsData(response);
    } catch (error) {
      console.error("Error fetching alerts:", error);
    } finally {
      setLoading(false);
    }
  }, [sortConfig]);

  // Effetto iniziale e al cambio di sort
  useEffect(() => {
    loadData();
  }, [loadData]);

  // LOGICA DI ORDINAMENTO (Asc -> Desc -> Null)
  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    } else if (sortConfig.key === key && sortConfig.direction === "desc") {
      direction = null;
    }
    setSortConfig({ key: direction ? key : null, direction });
  };

  // Helper per indicatore freccia
  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? " ▲" : " ▼";
  };

  const filteredAlerts = useMemo(() => {
    const dataToFilter = (alertsData && alertsData.result) || [];

    return dataToFilter.filter((alert) => {
      const classMatch = !classFilter || alert.alert_class === classFilter;
      const typeMatch =
        !typeFilter ||
        alert.alert_type.toLowerCase().includes(typeFilter.toLowerCase());
      const entityMatch =
        !entityFilter ||
        alert.entity.toLowerCase().includes(entityFilter.toLowerCase());
      const levelMatch =
        selectedLevel === 0 || alert.priority === selectedLevel;
      return classMatch && typeMatch && entityMatch && levelMatch;
    });
  }, [classFilter, typeFilter, entityFilter, selectedLevel, alertsData]);

  const virtualizer = useVirtualizer({
    count: filteredAlerts.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 20,
  });

  const renderEntityCell = (alert) => {
    const level = alert.entity_level ? alert.entity_level.toLowerCase() : "";
    const entityName = alert.entity;

    if (!entityName) return "N/A";

    if (level === "sku") {
      return (
        <Link
          to={`/sku/${encodeURIComponent(entityName)}?from=alerts`}
          className="highlight-link"
        >
          {entityName}
        </Link>
      );
    }

    if (level === "family") {
      return (
        <Link
          to={`/family/${encodeURIComponent(entityName)}?from=alerts`}
          className="highlight-link"
        >
          {entityName}
        </Link>
      );
    }

    return entityName;
  };

  const handleLevelClick = (level) => {
    setSelectedLevel((prevLevel) => (prevLevel === level ? 0 : level));
  };

  // Render condizionale durante il caricamento iniziale
  if (loading && !alertsData) {
    return (
      <div
        style={{
          color: "white",
          textAlign: "center",
          paddingTop: "5rem",
          fontSize: "1.5rem",
        }}
      >
        Loading Alerts...
      </div>
    );
  }

  // Se non ci sono dati o result è vuoto (ma non sta caricando)
  if (!alertsData || !alertsData.result) {
    return (
      <>
        <header>
          <h1>ALERTS</h1>
          <div className="header-buttons">
            <Link to="/" className="header-btn">
              GLOBAL VIEW
            </Link>
          </div>
        </header>
        <main className="dashboard-container">
          <div
            style={{ padding: "20px", textAlign: "center", fontSize: "1.2em" }}
          >
            No alerts data available.
          </div>
        </main>
      </>
    );
  }

  const rawAlertsArray = alertsData.result;
  const criticalAlertsCount = rawAlertsArray.filter(
    (a) => a.priority >= 3,
  ).length;
  const warningAlertsCount = rawAlertsArray.filter(
    (a) => a.priority < 3,
  ).length;
  const noFilteredResults = filteredAlerts.length === 0;

  return (
    <>
      <header>
        <h1>ALERTS</h1>
        <div className="header-buttons">
          <Link to="/" className="header-btn">
            GLOBAL VIEW
          </Link>
        </div>
      </header>

      <main className="dashboard-container">
        <section className="alert-summary-container">
          <div className="summary-box critical">
            <div className="summary-title">CRITICAL ALERTS</div>
            <div className="summary-value">{criticalAlertsCount}</div>
          </div>
          <div className="summary-box warning">
            <div className="summary-title">WARNING ALERTS</div>
            <div className="summary-value">{warningAlertsCount}</div>
          </div>
        </section>

        <div className="filter-section alert-filters">
          <span>Filter by</span>
          <select
            value={classFilter}
            onChange={(e) => setClassFilter(e.target.value)}
          >
            <option value="">All Classes</option>
            <option>Imported Data</option>
            <option>Variation</option>
            <option>Constraint</option>
          </select>
          <input
            type="text"
            placeholder="Search by Alert Type..."
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          />
          <input
            type="text"
            placeholder="Search by Entity..."
            value={entityFilter}
            onChange={(e) => setEntityFilter(e.target.value)}
          />

          <div
            className="alert-level-icons filter-chilis"
            onMouseLeave={() => setHoveredLevel(0)}
          >
            {[1, 2, 3, 4, 5].map((level) => {
              const isActive = hoveredLevel
                ? level <= hoveredLevel
                : level <= selectedLevel;

              return (
                <div
                  key={level}
                  onClick={() => handleLevelClick(level)}
                  onMouseEnter={() => setHoveredLevel(level)}
                  className={isActive ? "active" : ""}
                >
                  <ChiliIcon />
                </div>
              );
            })}
          </div>
        </div>

        <div className="main-table-container full-page-table">
          {noFilteredResults ? (
            <div
              style={{
                padding: "20px",
                textAlign: "center",
                fontSize: "1.2em",
              }}
            >
              **No alerts match the current filters.**
            </div>
          ) : (
            <div id="alert-table">
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: GRID_COLUMNS,
                  backgroundColor: "var(--table-header-color)",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                }}
              >
                {[
                  ["alert_class", "ALERT CLASS"],
                  ["alert_type", "ALERT TYPE"],
                  ["target", "TARGET VALUE"],
                  ["expected", "EXPECTED VALUE"],
                  ["actual", "ACTUAL VALUE"],
                  ["entity_level", "ENTITY LEVEL"],
                  ["entity", "ENTITY"],
                  ["priority", "ALERT LEVEL"],
                  ["date", "DATE"],
                ].map(([key, label]) => (
                  <div
                    key={key}
                    onClick={() => requestSort(key)}
                    style={{
                      cursor: "pointer",
                      userSelect: "none",
                      padding: "12px 8px",
                      fontWeight: "bold",
                      fontSize: "0.85em",
                      textTransform: "uppercase",
                      color: "var(--text-color)",
                    }}
                  >
                    {label}{getSortIndicator(key)}
                  </div>
                ))}
              </div>

              <div
                ref={scrollContainerRef}
                className="alert-scroll-body"
                style={{
                  overflowY: "auto",
                  maxHeight: "calc(79.5vh - 45px)",
                }}
              >
                <div
                  style={{
                    height: `${virtualizer.getTotalSize()}px`,
                    position: "relative",
                    opacity: loading ? 0.5 : 1,
                    transition: "opacity 0.2s",
                  }}
                >
                  {virtualizer.getVirtualItems().map((virtualRow) => {
                    const alert = filteredAlerts[virtualRow.index];
                    return (
                      <div
                        key={virtualRow.index}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: `${virtualRow.size}px`,
                          transform: `translateY(${virtualRow.start}px)`,
                          display: "grid",
                          gridTemplateColumns: GRID_COLUMNS,
                          alignItems: "center",
                          backgroundColor: "#1a4a70",
                          borderBottom: "1px solid rgba(255,255,255,0.05)",
                        }}
                        className="alert-virtual-row"
                      >
                        <div className="alert-cell" title={alert.alert_class}>{alert.alert_class}</div>
                        <div className="alert-cell" title={formatDatesInText(alert.alert_type)}>
                          {formatDatesInText(alert.alert_type)}
                        </div>
                        <div className="alert-cell">{formatAlertValue(alert.target)}</div>
                        <div className="alert-cell">{formatAlertValue(alert.expected)}</div>
                        <div className="alert-cell">{formatAlertValue(alert.actual)}</div>
                        <div className="alert-cell" title={alert.entity_level}>{alert.entity_level}</div>
                        <div className="alert-cell" title={alert.entity || ""}>{renderEntityCell(alert)}</div>
                        <div className="alert-cell">
                          <div className="alert-level-icons">
                            {Array.from({ length: alert.priority }).map((_, i) => (
                              <ChiliIcon key={i} />
                            ))}
                          </div>
                        </div>
                        <div className="alert-cell" title={formatAlertDate(alert.date)}>{formatAlertDate(alert.date)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
}

export default AlertingPage;
